// packages/core/src/economy/EarningRules.ts
// ─────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for what every action earns. Tune values here.
// Server and client must use the same numbers — that's why this is in core.
// ─────────────────────────────────────────────────────────────────────────

import type { EarnEventType } from './EconomyTypes';

/** Base reward per event type. Server validates and applies. */
export const EARN_REWARDS: Record<EarnEventType, number> = {
  daily_login:              5,
  streak_milestone:         0,   // computed by streakBonusFor()
  level_up:                25,
  quiz_pass_first_try:     20,
  quiz_pass_subsequent:     5,
  quiz_aced:               10,   // 100% score, on top of the pass reward
  module_complete:         50,
  impact_node_reward:      10,   // Core Path bonus decision node (Impact Lab V2)
  lesson_audio_complete:    3,
  playlist_complete:       10,
  final_test_pass:        100,
  discovery_quiz_complete: 10,   // once per Discovery quiz
  daily_review_cleared:     5,   // once per UTC day (date-scoped ledger key)
  lesson_applied:           5,   // DORMANT until the apply surface ships
  scenario_first_saved:    10,   // once ever
  market_replay_first:     10,   // once ever
  school_exhibit_complete: 10,   // once per school
  first_install:           25,
  iap_purchase:             0,   // amount comes from purchase package
};

/** Streak milestones — extra one-time bonus when hit. */
export const STREAK_MILESTONES: Array<{ days: number; bonus: number }> = [
  { days:   3, bonus:  10 },
  { days:   7, bonus:  25 },
  { days:  14, bonus:  50 },
  { days:  30, bonus: 100 },
  { days:  60, bonus: 200 },
  { days: 100, bonus: 500 },
  { days: 365, bonus: 2000 },
];

export function streakBonusFor(streakDays: number): number {
  const hit = STREAK_MILESTONES.find(m => m.days === streakDays);
  return hit ? hit.bonus : 0;
}

/**
 * Streak Freeze price (gems) — the ONLY gem spend at launch. One freeze
 * protects one missed day, applied automatically; freezes chain across
 * multi-day gaps up to the owned count. The inventory cap
 * (MAX_STREAK_FREEZES = 2) lives with the streak mechanics in
 * srs/gamification.ts — price is economy, capacity is streak.
 */
export const STREAK_FREEZE_COST = 100;

/** IAP packages. Pricing in USD. */
export const IAP_PACKAGES: Array<{
  id: string;
  gems: number;
  bonus: number;
  priceUsd: number;
  bestValue?: boolean;
}> = [
  { id: 'gems_100',  gems:  100, bonus:   0, priceUsd:  0.99 },
  { id: 'gems_500',  gems:  500, bonus:  50, priceUsd:  4.99 },
  { id: 'gems_1200', gems: 1200, bonus: 200, priceUsd:  9.99, bestValue: true },
  { id: 'gems_2500', gems: 2500, bonus: 500, priceUsd: 19.99 },
  { id: 'gems_6500', gems: 6500, bonus: 1500, priceUsd: 49.99 },
];

